import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { toast } from 'sonner';
import { Menu, Plus, Copy, RefreshCw, Ban, Eye, Download, Trash2, LogOut, Sparkles } from 'lucide-react';
import SwitchViewMenu from '../../components/SwitchViewMenu';
import { ResendInviteButton } from '../../components/ResendInviteButton';
import { AdvisorNotifications } from '../../components/AdvisorNotifications';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdvisorDashboard() {
  const [dashboard, setDashboard] = useState({ invites: [], submissions: [] });
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  // Retrieve-link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [retrievedLink, setRetrievedLink] = useState('');
  const [retrieving, setRetrieving] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/advisor/dashboard`, { withCredentials: true });
      setDashboard(data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleCreateInvite = async (sendEmail = true) => {
    try {
      const { data } = await axios.post(
        `${API}/advisor/invites`,
        { client_email: clientEmail, client_name: clientName, send_email: sendEmail },
        { withCredentials: true }
      );
      const fullLink = data.invite_url || `${window.location.origin}/f/${data.token}`;
      setGeneratedLink(fullLink);

      if (!sendEmail) {
        // Pre-fill first: jump straight into the wizard
        toast.success('Invite created — pre-fill the form, then send.');
        setCreateDialogOpen(false);
        setClientEmail('');
        setClientName('');
        navigate(`/advisor/invites/${data.id}/prefill`);
        return;
      }

      if (data.email_sent) {
        toast.success(`Invite sent to ${clientEmail}`);
      } else {
        // Copy link to clipboard so advisor can share manually
        try { await navigator.clipboard.writeText(fullLink); } catch (err) { console.warn('clipboard write failed:', err); }
        const reason = data.email_error
          ? `Email delivery failed: ${data.email_error}`
          : 'Email delivery is not configured.';
        toast.warning(`${reason} Link copied to clipboard — share it with the client manually.`, { duration: 12000 });
      }
      loadDashboard();
      setClientEmail('');
      setClientName('');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success('Link copied to clipboard');
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/advisor/invites/${inviteId}/revoke`, {}, { withCredentials: true });
      toast.success('Invite revoked');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDeleteInvite = async (inviteId, clientEmail) => {
    if (!window.confirm(`Permanently delete invite for ${clientEmail}? Any pending draft will be lost. Submitted forms are preserved.`)) return;
    try {
      await axios.delete(`${API}/advisor/invites/${inviteId}`, { withCredentials: true });
      toast.success('Invite deleted');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openInviteLinkDialog = (invite) => {
    setSelectedInvite(invite);
    setRetrievedLink('');
    setLinkDialogOpen(true);
  };

  const retrieveInviteLink = async () => {
    if (!selectedInvite) return;
    setRetrieving(true);
    try {
      const { data } = await axios.post(
        `${API}/advisor/invites/${selectedInvite.id}/regenerate`,
        {},
        { withCredentials: true }
      );
      const fullLink = data.invite_url || `${window.location.origin}/f/${data.token}`;
      setRetrievedLink(fullLink);
      try { await navigator.clipboard.writeText(fullLink); } catch (err) { console.warn("suppressed error:", err); }
      if (data.email_sent) {
        toast.success('Fresh link generated and re-emailed to the client');
      } else {
        toast.success('Fresh link generated and copied to clipboard');
      }
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRetrieving(false);
    }
  };

  const copyRetrievedLink = () => {
    if (retrievedLink) {
      navigator.clipboard.writeText(retrievedLink);
      toast.success('Copied');
    }
  };

  const handleViewSubmission = (submissionId) => {
    navigate(`/advisor/submission/${submissionId}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/advisor/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      opened: 'bg-blue-100 text-blue-800',
      submitted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || ''}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  /** Countdown badge coloured by urgency. Returns null for terminal statuses. */
  const getExpiryBadge = (expiresAt, status) => {
    if (!expiresAt) return <span className="text-xs text-[#94a3b8]">—</span>;
    const terminal = status === 'submitted' || status === 'revoked';
    const dateLabel = new Date(expiresAt).toLocaleDateString();
    if (terminal) {
      return <span className="text-xs text-[#94a3b8]">{dateLabel}</span>;
    }
    const now = new Date();
    const end = new Date(expiresAt);
    const msLeft = end.getTime() - now.getTime();
    const daysLeft = Math.floor(msLeft / 86400000);
    const hoursLeft = Math.floor(msLeft / 3600000);

    let label;
    let tone;
    let icon = '⏱';
    if (msLeft <= 0 || status === 'expired') {
      label = 'Expired';
      tone = 'bg-red-100 text-red-800 border-red-200';
      icon = '⚠';
    } else if (daysLeft === 0) {
      label = hoursLeft <= 1 ? `Expires in ${Math.max(1, Math.floor(msLeft / 60000))}m` : `Expires in ${hoursLeft}h`;
      tone = 'bg-red-100 text-red-800 border-red-200';
      icon = '⚠';
    } else if (daysLeft <= 1) {
      label = 'Expires in 1 day';
      tone = 'bg-red-100 text-red-800 border-red-200';
      icon = '⚠';
    } else if (daysLeft <= 3) {
      label = `Expires in ${daysLeft} days`;
      tone = 'bg-orange-100 text-orange-800 border-orange-200';
    } else if (daysLeft <= 7) {
      label = `Expires in ${daysLeft} days`;
      tone = 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else {
      label = `Expires in ${daysLeft} days`;
      tone = 'bg-[#f8fafc] text-[#475569] border-[#e2e8f0]';
    }
    return (
      <div className="flex flex-col gap-0.5" data-testid={`invite-expiry-badge`}>
        <span
          className={`inline-flex items-center gap-1 self-start px-2 py-0.5 text-xs font-medium rounded-full border ${tone}`}
          title={`Expires ${dateLabel}`}
        >
          <span aria-hidden>{icon}</span> {label}
        </span>
        <span className="text-[10px] text-[#94a3b8]">{dateLabel}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-10 h-10 rounded-md shadow-sm" data-testid="brand-logo" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
              <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
              <span style={{ fontWeight: 300 }}>WEALTH</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-white/90 hover:text-white text-sm underline"
                data-testid="admin-panel-link"
              >
                Admin Panel
              </button>
            )}
            <span className="text-white text-sm" data-testid="advisor-name">{user?.name}</span>
            <AdvisorNotifications />
            <SwitchViewMenu current="advisor" roleLabel="ADVISOR" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-heading">
              Dashboard
            </h2>
            <p className="text-[#475569] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Manage client invites and submissions
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white" data-testid="create-invite-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Invite
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Client Invite</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="client-email">Client Email *</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="mt-2"
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="client-name">Client Name (Optional)</Label>
                  <Input
                    id="client-name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="John Doe"
                    className="mt-2"
                    data-testid="invite-name-input"
                  />
                </div>
                {generatedLink && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm font-medium text-green-900 mb-2">Invite Link Generated:</p>
                    <div className="flex gap-2">
                      <Input value={generatedLink} readOnly className="text-sm" data-testid="generated-link" />
                      <Button onClick={copyLink} size="sm" data-testid="copy-link-button">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleCreateInvite(false)}
                    disabled={!clientEmail}
                    variant="outline"
                    className="border-[#5b93d6] text-[#0f2444] hover:bg-[#f8fafc]"
                    data-testid="submit-invite-prefill-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create & Pre-fill First
                  </Button>
                  <Button
                    onClick={() => handleCreateInvite(true)}
                    disabled={!clientEmail}
                    className="bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90"
                    data-testid="submit-invite-button"
                  >
                    Create & Send Invite
                  </Button>
                </div>
                <p className="text-xs text-[#475569]">
                  Pre-fill first lets you complete any fields you already know before emailing the client the form link.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Invites Section */}
        <div className="mb-12">
          <h3 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="invites-section">
            Client Invites ({dashboard.invites.length})
          </h3>
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full portal-table">
                <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {dashboard.invites.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#475569]">
                        No invites yet. Create your first invite to get started.
                      </td>
                    </tr>
                  ) : (
                    dashboard.invites.map((invite) => (
                      <tr key={invite.id} className="hover:bg-[#f8fafc] transition-colors" data-testid={`invite-row-${invite.id}`}>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => openInviteLinkDialog(invite)}
                            className="text-[#0f172a] font-medium hover:text-[#5b93d6] hover:underline text-left cursor-pointer"
                            data-testid={`invite-name-link-${invite.id}`}
                            title="Click to retrieve invite link"
                          >
                            {invite.client_name || invite.client_email}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Email">{invite.client_email}</td>
                        <td className="px-6 py-4" data-label="Status">{getStatusBadge(invite.status)}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          {new Date(invite.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          {getExpiryBadge(invite.expires_at, invite.status)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            {invite.status !== 'submitted' && invite.status !== 'revoked' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/advisor/invites/${invite.id}/prefill`)}
                                  data-testid={`prefill-button-${invite.id}`}
                                  title="Pre-fill form fields for this client"
                                >
                                  <Sparkles className="w-4 h-4 text-[#5b93d6]" />
                                </Button>
                                <ResendInviteButton
                                  endpoint={`/advisor/invites/${invite.id}/resend`}
                                  recipient={invite.client_email}
                                  onResent={loadDashboard}
                                  testId={`resend-invite-${invite.id}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeInvite(invite.id)}
                                  data-testid={`revoke-button-${invite.id}`}
                                  title="Revoke"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvite(invite.id, invite.client_email)}
                              data-testid={`delete-invite-button-${invite.id}`}
                              title="Delete invite"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Submissions Section */}
        <div>
          <h3 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="submissions-section">
            Submitted Forms ({dashboard.submissions.length})
          </h3>
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full portal-table">
                <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {dashboard.submissions.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-[#475569]">
                        No submissions yet.
                      </td>
                    </tr>
                  ) : (
                    dashboard.submissions.map((submission) => (
                      <tr key={submission.id} className="hover:bg-[#f8fafc] transition-colors" data-testid={`submission-row-${submission.id}`}>
                        <td className="px-6 py-4 text-sm text-[#0f172a]">{submission.client_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">{submission.client_email}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSubmission(submission.id)}
                            data-testid={`view-submission-button-${submission.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Retrieve Invite Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Link — {selectedInvite?.client_name || selectedInvite?.client_email}</DialogTitle>
            <DialogDescription>
              Retrieve a fresh invite link, or delete this invite entirely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              For security, invite tokens are stored one-way and cannot be recovered. Click below to generate a
              <strong> fresh </strong> link (the previous link will stop working) and email it to the client.
            </div>
            <div className="text-sm text-[#475569] space-y-1">
              <div><strong>Client:</strong> {selectedInvite?.client_name || '—'}</div>
              <div><strong>Email:</strong> {selectedInvite?.client_email}</div>
              <div><strong>Status:</strong> {selectedInvite?.status?.toUpperCase()}</div>
              <div><strong>Expires:</strong> {selectedInvite ? getExpiryBadge(selectedInvite.expires_at, selectedInvite.status) : '—'}</div>
            </div>

            {!retrievedLink ? (
              <Button
                onClick={retrieveInviteLink}
                disabled={retrieving || selectedInvite?.status === 'submitted' || selectedInvite?.status === 'revoked'}
                className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90"
                data-testid="retrieve-link-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${retrieving ? 'animate-spin' : ''}`} />
                {retrieving ? 'Generating…' : 'Generate & Copy Fresh Link'}
              </Button>
            ) : (
              <div className="rounded-md border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-medium text-green-900 mb-2">Fresh link ready (copied to clipboard):</p>
                <div className="flex gap-2">
                  <Input value={retrievedLink} readOnly className="text-sm" data-testid="retrieved-link-input" />
                  <Button onClick={copyRetrievedLink} size="sm" data-testid="copy-retrieved-link-button">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {selectedInvite && selectedInvite.status !== 'submitted' && selectedInvite.status !== 'revoked' && (
              <Button
                variant="outline"
                onClick={() => { handleDeleteInvite(selectedInvite.id, selectedInvite.client_email); setLinkDialogOpen(false); }}
                className="w-full text-red-600 hover:text-red-700 border-red-200"
                data-testid="dialog-delete-invite-button"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete this invite
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
